import React, { useState } from "react";
import api from "../lib/api";
import { Loader2, Check, Tag, X } from "lucide-react";

/**
 * PromoCodeInput — collapsible input that sends a code to /api/promo/redeem.
 * Requires the user to be signed in (caller must guard with `enabled` if not).
 */
const PromoCodeInput = ({ onApplied, compact = false }) => {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!code.trim()) return;
    setError("");
    setSuccess("");
    setBusy(true);
    try {
      const { data } = await api.post("/promo/redeem", { code: code.trim() });
      setSuccess(data?.message || "Code applied — enjoy!");
      setCode("");
      onApplied?.(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not apply this code.");
    } finally {
      setBusy(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid="promo-success"
        className="inline-flex items-center gap-2 px-3 py-2 rounded-[8px] bg-[#1D9E75]/10 border border-[#1D9E75]/40 text-[#1D9E75] text-sm"
      >
        <Check className="w-4 h-4" /> {success}
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="promo-toggle-btn"
        className={`inline-flex items-center gap-1.5 text-xs underline-offset-4 hover:underline ${
          compact ? "text-[#5F5E5A]" : "text-[#534AB7]"
        }`}
      >
        <Tag className="w-3 h-3" /> Have a promo code?
      </button>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-2" data-testid="promo-form">
      <div className="flex items-center gap-1.5">
        <Tag className="w-3.5 h-3.5 text-[#534AB7]" />
        <span className="text-xs uppercase tracking-[0.18em] text-[#5F5E5A]">Promo code</span>
        <button
          type="button"
          onClick={() => { setOpen(false); setError(""); setCode(""); }}
          className="ml-auto p-0.5 text-[#5F5E5A] hover:text-[#2C2C2A]"
          aria-label="Close"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="flex gap-1.5">
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="LAUNCH50"
          data-testid="promo-input"
          className="flex-1 px-3 py-2 rounded-[8px] bg-white border border-[#D3D1C7] text-sm font-mono uppercase tracking-wide text-[#2C2C2A] focus:outline-none focus:border-[#534AB7]"
        />
        <button
          type="submit"
          disabled={!code.trim() || busy}
          data-testid="promo-apply-btn"
          className="px-4 py-2 rounded-[8px] bg-[#534AB7] text-white text-sm font-medium hover:bg-[#3C3489] disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          Apply
        </button>
      </div>
      {error && <p className="text-xs text-[#BA7517]" data-testid="promo-error">{error}</p>}
    </form>
  );
};

export default PromoCodeInput;
